import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Form, Modal, Pagination, Row, Spinner, Tab, Table, Tabs } from 'react-bootstrap';
import { faBan, faKey, faPenToSquare, faTrash } from '@fortawesome/free-solid-svg-icons';
import ActionIconButton from '../../components/ActionIconButton';
import {
  deactivateManagedUser,
  deleteManagedUser,
  getManagedUsers,
  getRolesPermissions,
  resetManagedUserPassword,
  saveManagedUser,
  saveRolePermissions,
} from '../../service/apiService';
import './UserManagement.css';

const defaultUser = { id: 0, name: '', email: '', password: '', role: 'Staff', status: 'active' };
const permissionActions = ['view', 'add', 'edit', 'delete'];

function ManageUsers() {
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total_pages: 1 });
  const [filters, setFilters] = useState({ search: '', role: '', status: '' });
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [rolePermissions, setRolePermissions] = useState({});
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [userForm, setUserForm] = useState(defaultUser);
  const [showUserModal, setShowUserModal] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const selectedRole = useMemo(() => roles.find((role) => String(role.id) === String(selectedRoleId)), [roles, selectedRoleId]);
  const selectedPermissions = useMemo(() => rolePermissions[selectedRoleId] || {}, [rolePermissions, selectedRoleId]);
  const roleOptions = roles.map((role) => role.name);

  const loadUsers = async (page = pagination.page) => {
    setLoading(true);
    try {
      const res = await getManagedUsers({ ...filters, page, limit: pagination.limit });
      if (res.status === 'success') {
        setUsers(res.data.users || []);
        setPagination(res.data.pagination || pagination);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    const res = await getRolesPermissions();
    if (res.status === 'success') {
      setRoles(res.data.roles || []);
      setPermissions(res.data.permissions || []);
      setRolePermissions(res.data.role_permissions || {});
      if (!selectedRoleId && res.data.roles?.[0]) setSelectedRoleId(String(res.data.roles[0].id));
    }
  };

  useEffect(() => {
    loadUsers(1);
  }, [filters]);

  useEffect(() => {
    loadRoles();
  }, []);

  const openCreateUser = () => {
    setUserForm(defaultUser);
    setShowUserModal(true);
  };

  const openEditUser = (user) => {
    setUserForm({ ...defaultUser, ...user, password: '' });
    setShowUserModal(true);
  };

  const handleSaveUser = async (event) => {
    event.preventDefault();
    const action = userForm.id ? 'update' : 'create';
    const payload = { ...userForm, action };
    if (userForm.id) delete payload.password;
    const res = await saveManagedUser(payload);
    if (res.status === 'success') {
      setShowUserModal(false);
      await loadUsers();
      await loadRoles();
    } else {
      alert(res.message || 'Unable to save user');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword) return alert('Enter a new password');
    const res = await resetManagedUserPassword(resetUser.id, newPassword);
    if (res.status === 'success') {
      setResetUser(null);
      setNewPassword('');
    } else {
      alert(res.message || 'Unable to reset password');
    }
  };

  const handlePermissionChange = (moduleKey, action, checked) => {
    setRolePermissions((prev) => ({
      ...prev,
      [selectedRoleId]: {
        ...(prev[selectedRoleId] || {}),
        [moduleKey]: {
          ...((prev[selectedRoleId] || {})[moduleKey] || {}),
          [action]: checked,
        },
      },
    }));
  };

  const handleSaveRole = async () => {
    if (!selectedRole) return;
    const res = await saveRolePermissions({
      id: selectedRole.id,
      name: selectedRole.name,
      status: selectedRole.status,
      permissions: selectedPermissions,
    });
    if (res.status === 'success') await loadRoles();
    else alert(res.message || 'Unable to save role');
  };

  return (
    <div className="user-management-page">
      <div className="page-title-row">
        <div>
          <span>Admin</span>
          <h3>User Management</h3>
        </div>
        <Button onClick={openCreateUser}>Add User</Button>
      </div>

      <Tabs defaultActiveKey="users" className="erp-tabs mb-3">
        <Tab eventKey="users" title="Users">
          <Card className="erp-panel-card">
            <Card.Body>
              <Row className="g-2 mb-3">
                <Col md={5}><Form.Control placeholder="Search name or email" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} /></Col>
                <Col md={3}>
                  <Form.Select value={filters.role} onChange={(e) => setFilters({ ...filters, role: e.target.value })}>
                    <option value="">All roles</option>
                    {roleOptions.map((role) => <option key={role} value={role}>{role}</option>)}
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                    <option value="">All status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Form.Select>
                </Col>
              </Row>

              {loading ? <div className="table-loader"><Spinner animation="border" /></div> : (
                <Table responsive hover className="erp-table">
                  <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Actions</th></tr></thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td>{user.name}</td>
                        <td>{user.email}</td>
                        <td>{user.role}</td>
                        <td><Badge bg={(user.status || 'active') === 'active' ? 'success' : 'secondary'}>{user.status || 'active'}</Badge></td>
                        <td>
                          <div className="action-row">
                            <ActionIconButton size="sm" variant="outline-primary" icon={faPenToSquare} label="Edit user" onClick={() => openEditUser(user)} />
                            <ActionIconButton size="sm" variant="outline-secondary" icon={faKey} label="Reset password" onClick={() => setResetUser(user)} />
                            <ActionIconButton size="sm" variant="outline-warning" icon={faBan} label="Deactivate user" onClick={() => deactivateManagedUser(user.id).then(() => loadUsers())} />
                            <ActionIconButton size="sm" variant="outline-danger" icon={faTrash} label="Delete user" onClick={() => window.confirm('Delete this user?') && deleteManagedUser(user.id).then(() => loadUsers())} />
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!users.length && <tr><td colSpan="5" className="text-center text-muted">No users found</td></tr>}
                  </tbody>
                </Table>
              )}

              {pagination.total_pages > 1 && (
                <Pagination className="justify-content-end">
                  <Pagination.Prev disabled={pagination.page <= 1} onClick={() => loadUsers(pagination.page - 1)} />
                  {[...Array(pagination.total_pages)].map((_, index) => (
                    <Pagination.Item key={index + 1} active={pagination.page === index + 1} onClick={() => loadUsers(index + 1)}>{index + 1}</Pagination.Item>
                  ))}
                  <Pagination.Next disabled={pagination.page >= pagination.total_pages} onClick={() => loadUsers(pagination.page + 1)} />
                </Pagination>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="roles" title="Roles & Permissions">
          <Card className="erp-panel-card">
            <Card.Body>
              <Row className="g-3">
                <Col lg={3}>
                  <div className="role-list">
                    {roles.map((role) => (
                      <button key={role.id} className={String(role.id) === String(selectedRoleId) ? 'active' : ''} onClick={() => setSelectedRoleId(String(role.id))}>
                        <strong>{role.name}</strong>
                        <span>{role.status}</span>
                      </button>
                    ))}
                  </div>
                </Col>
                <Col lg={9}>
                  <div className="permission-header">
                    <div>
                      <span>Role</span>
                      <h5>{selectedRole?.name || 'Select role'}</h5>
                    </div>
                    <Button onClick={handleSaveRole} disabled={!selectedRole}>Save Permissions</Button>
                  </div>
                  <Table responsive bordered className="permission-table">
                    <thead><tr><th>Module</th>{permissionActions.map((action) => <th key={action}>{action}</th>)}</tr></thead>
                    <tbody>
                      {permissions.map((permission) => (
                        <tr key={permission.module_key}>
                          <td>{permission.module_name}</td>
                          {permissionActions.map((action) => (
                            <td key={action}>
                              <Form.Check
                                type="checkbox"
                                checked={!!selectedPermissions?.[permission.module_key]?.[action]}
                                onChange={(e) => handlePermissionChange(permission.module_key, action, e.target.checked)}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      <Modal show={showUserModal} onHide={() => setShowUserModal(false)} centered>
        <Form onSubmit={handleSaveUser}>
          <Modal.Header closeButton><Modal.Title>{userForm.id ? 'Edit User' : 'Add User'}</Modal.Title></Modal.Header>
          <Modal.Body>
            <Row className="g-3">
              <Col md={6}><Form.Label>Name</Form.Label><Form.Control value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} required /></Col>
              <Col md={6}><Form.Label>Email</Form.Label><Form.Control type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} required /></Col>
              {!userForm.id && <Col md={6}><Form.Label>Password</Form.Label><Form.Control type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} required /></Col>}
              <Col md={6}><Form.Label>Role</Form.Label><Form.Select value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}>{roleOptions.map((role) => <option key={role} value={role}>{role}</option>)}</Form.Select></Col>
              <Col md={6}><Form.Label>Status</Form.Label><Form.Select value={userForm.status} onChange={(e) => setUserForm({ ...userForm, status: e.target.value })}><option value="active">Active</option><option value="inactive">Inactive</option></Form.Select></Col>
            </Row>
          </Modal.Body>
          <Modal.Footer><Button variant="secondary" onClick={() => setShowUserModal(false)}>Cancel</Button><Button type="submit">Save</Button></Modal.Footer>
        </Form>
      </Modal>

      <Modal show={!!resetUser} onHide={() => setResetUser(null)} centered>
        <Modal.Header closeButton><Modal.Title>Reset Password</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Label>New password for {resetUser?.name}</Form.Label>
          <Form.Control type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setResetUser(null)}>Cancel</Button><Button onClick={handleResetPassword}>Reset Password</Button></Modal.Footer>
      </Modal>
    </div>
  );
}

export default ManageUsers;
